import React, { useCallback, useRef, useState } from 'react';
import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import { SearchBar } from 'new/elements/flows/EnterCredential/components/SearchBar';
import { CredentialCard } from 'new/elements/flows/EnterCredential/components/CredentialCard';
import { AddCredentialCard } from 'new/elements/flows/EnterCredential/components/AddCredentialCard';
import { useDistance } from 'new/hooks/useDistance';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { FCFormPageType } from 'new/elements/Form/useForm';
import useConnectionCredentials from 'new/hooks/adapters/useConnectionCredentials';
import { Button, CircularProgress } from '@mui/material';
import { ModalDefault } from 'new/routes/ModalDefault';
import { ModalAddCredential } from 'new/elements/flows/ModalAddCredential';
import { ModalEditCredential } from 'new/elements/flows/ModalEditCredential';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_CREDENTIALS_REQUEST } from 'js/constants/actionTypes';
import { ReactComponent as RefreshIcon } from 'src/new/components/icons/refresh.svg';
import { isReadOnly } from 'js/lib/ACLUtils';
import { CredentialSaveResult } from 'new/components/flows/controller/useCredentialsPage';
import { useSnackbar } from 'new/contexts/SnackbarContext';

import classes from './EnterCredential.module.scss';

const SAVE_CREDENTIAL_DEBOUNCE_MS = 300;

type EnterConnectionPropsBase = {
  connectionType?: string;
  credentialId?: any;
  selectedCredentialInfo?: any;
  selectedTemplate?: string;
  onCredentialSelected?: (credential: any) => void;
  onChangeTemplate?: (template: any) => void;
  onCredentialInfoUpdated?: (newInfo, field, value) => void;
  onSaveCredential?: () => Promise<CredentialSaveResult>;
  onEditCredential?: () => Promise<any>;
};

type EnterConnectionProps = EnterConnectionPropsBase &
  (
    | { reload: () => void; areCredentialsLoading: boolean }
    | { reload?: undefined; areCredentialsLoading?: never }
  );

export const EnterCredential: FCFormPageType<EnterConnectionProps> = (
  props: EnterConnectionProps,
) => {
  const {
    connectionType,
    onCredentialInfoUpdated,
    onChangeTemplate,
    selectedTemplate,
    onSaveCredential,
    onEditCredential,
    selectedCredentialInfo,
    areCredentialsLoading,
  } = props;
  const ref = useRef();
  const [showModalAdd, setShowModalAdd] = useState(false);
  const [editCredentialId, setShowModalEdit] = useState(null);
  const { showSuccess } = useSnackbar();

  const credentialsToDisplay = useConnectionCredentials(connectionType);
  const { filteredData: credentials, onSearchChange } = useFilteredData(
    credentialsToDisplay,
    'name',
  );
  const height = useDistance(ref, 400);
  const loading = useIsLoading(GET_CREDENTIALS_REQUEST);

  const onSave = useCallback(async () => {
    if (onSaveCredential) {
      const result = await onSaveCredential();
      if (!result?.success) return;
    }
    setShowModalAdd(false);
  }, [onSaveCredential]);

  const debouncedOnSave = useDebouncedCallback(onSave, [onSave], SAVE_CREDENTIAL_DEBOUNCE_MS, {
    throttle: true,
  });

  const onSaveEdit = async () => {
    if (onEditCredential) {
      const result = await onEditCredential();
      if (result?.saveError) {
        // Save failed — error already shown inside the edit form, keep modal open
        return;
      }
      // Probe failure surfaces on the credential card's Invalid badge (no extra toast);
      // save success shows a brief confirmation.
      if (result?.success) {
        showSuccess('Credential saved successfully');
      }
    }
    setShowModalEdit(null);
  };

  const onClickAdd = () => {
    setShowModalAdd(true);
  };

  const onHideModalEdit = () => setShowModalEdit(null);
  const onClickEdit = (id) => () => setShowModalEdit(id);
  const reloadCredentials = () => props?.reload?.();

  const isSelectionEmpty = props.credentialId === undefined;

  return (
    <Stack flex={1} height="100%">
      <SearchBar ref={ref} onSearchChange={onSearchChange} />

      <div className={classes.container} style={{ height }}>
        <div className={classes.headerContainer}>
          <Typography variant="overline">Add or Select a Credential</Typography>
          {props.reload ? (
            <Button
              color="info"
              variant="outlined"
              onClick={reloadCredentials}
              className={classes.refreshButton}
            >
              {areCredentialsLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <>
                  <RefreshIcon className={classes.refreshIcon} />
                  Refresh
                </>
              )}
            </Button>
          ) : null}
        </div>
        <div className={classes.credentials}>
          <AddCredentialCard onClick={onClickAdd} />
          {loading ? (
            <Stack alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          ) : null}
          {credentials.map((credential) => {
            const uniqueKey = `${credential?.id}_${credential?.name}`;
            return (
              <CredentialCard
                key={uniqueKey}
                isSelectionEmpty={isSelectionEmpty}
                onClickEdit={!isReadOnly(credential) ? onClickEdit(credential.id) : undefined}
                selected={props.credentialId === credential.id}
                onCredentialSelected={props.onCredentialSelected}
                credential={credential}
              />
            );
          })}
        </div>
      </div>

      {showModalAdd ? (
        <ModalAddCredential
          connectionType={connectionType}
          onCredentialInfoUpdated={onCredentialInfoUpdated}
          onSaveCredential={debouncedOnSave}
          onChangeTemplate={onChangeTemplate}
          selectedTemplate={selectedTemplate}
          onCloseClicked={() => setShowModalAdd(false)}
          selectedCredentialInfo={selectedCredentialInfo}
        />
      ) : null}

      <ModalDefault
        removeContentPadding
        title="Edit credential"
        open={!!editCredentialId}
        rootClassName={classes.modal_root_override}
        onCancel={onHideModalEdit}
        config={{ disableClickOut: true }}
      >
        <ModalEditCredential
          selectedTemplate={selectedTemplate}
          onChangeTemplate={onChangeTemplate}
          onSaveCredential={onSaveEdit}
          credentialId={editCredentialId}
        />
      </ModalDefault>
    </Stack>
  );
};

EnterCredential.formConfig = {
  title: 'Authenticate',
};
